import type { ReactNode } from "react";
import { Brand } from "../common/Brand.tsx";
import * as s from "./onboarding-shell.styles.ts";

interface OnboardingShellProps {
  stepNumber: number;
  totalSteps: number;
  // Optional context label (e.g. "Cannot continue", "Region not supported").
  // The default progress label `STEP X / Y` always renders.
  eyebrow?: string;
  title: string;
  description?: ReactNode;
  children: ReactNode;
}

// Shared frame for every onboarding gate page (age → jurisdiction → Safe Mode
// → platform → Phantom Bet activation). Renders the dark card, brand mark,
// step indicator dots, optional context eyebrow, headline, optional body, and
// a slot for the page-specific form / blocked view.
export function OnboardingShell({
  stepNumber,
  totalSteps,
  eyebrow,
  title,
  description,
  children,
}: OnboardingShellProps): JSX.Element {
  return (
    <div style={s.wrap} className="sz-onboarding-wrap">
      <div style={s.card} className="sz-onboarding-card">
        <div style={s.brandRow}>
          <Brand />
          <div style={s.stepIndicator}>
            <div style={s.stepDots} aria-hidden="true">
              {Array.from({ length: totalSteps }).map((_, idx) => {
                const n = idx + 1;
                const dotStyle =
                  n === stepNumber ? s.stepDotActive : n < stepNumber ? s.stepDotDone : s.stepDot;
                return <span key={n} style={dotStyle} />;
              })}
            </div>
            <span style={s.stepLabel}>
              Step {stepNumber} / {totalSteps}
            </span>
          </div>
        </div>
        {eyebrow ? <span style={s.eyebrow}>{eyebrow}</span> : null}
        <h1 style={s.title}>{title}</h1>
        {description ? <p style={s.description}>{description}</p> : null}
        {children}
      </div>
    </div>
  );
}
